import { DotsThreeIcon } from "@phosphor-icons/react/dist/csr/DotsThree";
import {
  matchPath,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import {
  PageContainer,
  PageHeader,
} from "@volca/ui/components/ui/page-components";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useGetBusiness } from "@/business/query";
import {
  ownersWriteTemplates,
  TEMPLATES_BY_VOLCA,
  useCanManageProfitability,
} from "@/profitability/access";
import { NewRunPage } from "@/profitability/new-run";
import { ArchivedTemplates } from "@/profitability/profitability-page/archived-templates";
import { ReportBuilder } from "@/profitability/report-builder";
import { ReportList } from "@/profitability/report-list";
import {
  PROFITABILITY_NEW_TEMPLATE_PATH,
  LEGACY_REPORTS_PATH,
  profitabilityNewRunPath,
  profitabilityTemplatePath,
} from "@/profitability/routes";
import {
  TEMPLATE_CHAT_STARTER_DESCRIPTION,
  TemplateChatStart,
  TemplateChatStarter,
} from "@/profitability/template-chat";
import {
  TemplatePage,
  TemplateSettingsPage,
} from "@/profitability/template-page";

export const JobCostingContent = ({ businessId }: { businessId: string }) => {
  const { reportId, section, settingsSection } = useParams();
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  // A manager a template was shared with reads it; setting one up, running
  // it and the archive are finance's (Profitability::Access).
  const canManage = useCanManageProfitability();
  const business = useGetBusiness(businessId);
  // The API refuses a new template while ServiceTitan reporting is on.
  const authoring = canManage && ownersWriteTemplates(business.data);
  if (authoring && pathname === `${PROFITABILITY_NEW_TEMPLATE_PATH}/form`)
    return <ReportBuilder businessId={businessId} />;
  if (authoring && pathname === PROFITABILITY_NEW_TEMPLATE_PATH)
    return <TemplateChatStarter businessId={businessId} />;
  if (
    canManage &&
    reportId &&
    matchPath(profitabilityNewRunPath(":reportId"), pathname)
  )
    return (
      <NewRunPage key={reportId} businessId={businessId} reportId={reportId} />
    );
  // The tab was called Trends. An old link opens Insights and the address
  // follows, keeping the period and anything the chat was carrying.
  if (reportId && section === "trends")
    return (
      <Navigate
        replace
        to={{
          pathname: `${profitabilityTemplatePath(reportId)}/insights`,
          search: location.search,
          hash: location.hash,
        }}
        state={location.state}
      />
    );
  // Settings is a page of its own.
  if (reportId && (section === "settings" || settingsSection))
    return (
      <TemplateSettingsPage
        key={reportId}
        businessId={businessId}
        reportId={reportId}
      />
    );
  if (reportId)
    return (
      <TemplatePage
        key={reportId}
        businessId={businessId}
        reportId={reportId}
      />
    );
  return (
    <PageContainer>
      <PageHeader
        title="Job Costing"
        description={authoring ? TEMPLATE_CHAT_STARTER_DESCRIPTION : undefined}
        actions={
          !canManage ? null : (
            <Stack direction="row" align="center" gap={2}>
              {authoring ? (
                <Button
                  onClick={() => navigate(PROFITABILITY_NEW_TEMPLATE_PATH)}
                >
                  New agent
                </Button>
              ) : (
                <Text variant="caption" tone="muted">
                  {TEMPLATES_BY_VOLCA}
                </Text>
              )}
              {business.data?.profitability_workflows?.includes(1) && (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        variant="secondary"
                        size="icon"
                        aria-label="More report options"
                      />
                    }
                  >
                    <DotsThreeIcon />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-56">
                    <DropdownMenuItem
                      onClick={() => navigate(LEGACY_REPORTS_PATH)}
                    >
                      ServiceTitan report history
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </Stack>
          )
        }
      />
      {/* The owner starts a template by describing it, right above the
          templates they already have. */}
      {authoring && <TemplateChatStart businessId={businessId} />}
      <ReportList
        businessId={businessId}
        canAuthor={ownersWriteTemplates(business.data)}
      />
      {canManage && <ArchivedTemplates businessId={businessId} />}
    </PageContainer>
  );
};
